﻿
// CourseCatalog.js
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  ScrollView,
  FlatList,
  Image,
  Dimensions,
  TextInput,
  Alert,
  Platform,
  Animated,
  Modal,
  RefreshControl,
  ActivityIndicator,
  SectionList,
  TouchableWithoutFeedback,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { 
  MaterialCommunityIcons, 
  Ionicons, 
  Feather,
  AntDesign,
  FontAwesome5,
  Entypo,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { Video } from 'expo-video';
import { Audio } from 'expo-audio'; // if you need audio
import { BlurView } from 'expo-blur';

const { width, height } = Dimensions.get('window');

const CourseCatalog = () => {
  const navigation = useNavigation();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseDetails, setShowCourseDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    category: 'all',
    level: 'all',
    price: 'all',
    rating: 0,
    duration: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [bookmarkedCourses, setBookmarkedCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [selectedPreviewVideo, setSelectedPreviewVideo] = useState(null);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'trending', 'new', 'recommended'
  
  const videoRef = useRef(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scrollY = useRef(new Animated.Value(0)).current;
  const searchTimeoutRef = useRef(null);

  // Animation for header
  const headerHeight = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [180, 100],
    extrapolate: 'clamp',
  });

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 80],
    outputRange: [1, 0.9],
    extrapolate: 'clamp',
  });

  useFocusEffect(
    React.useCallback(() => {
      loadInitialData();
      startAnimations();
      return () => {
        if (videoRef.current) {
          videoRef.current.unloadAsync();
        }
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, [])
  );

  useEffect(() => {
    filterCourses();
  }, [searchQuery, selectedFilters, courses, activeTab]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load preferences
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
      
      // Load all data
      await Promise.all([
        loadCourses(),
        loadBookmarks(),
        loadEnrolledCourses(),
      ]);
      
    } catch (error) {
      console.error('Error loading initial data:', error);
      Alert.alert('Error', 'Failed to load course catalog');
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      // Try to load from AsyncStorage first
      const savedCourses = await AsyncStorage.getItem('courseCatalog');
      if (savedCourses) {
        const parsedCourses = JSON.parse(savedCourses);
        setCourses(parsedCourses);
        setFilteredCourses(parsedCourses);
      } else {
        // Load mock courses
        const mockCourses = await generateMockCourses();
        setCourses(mockCourses);
        setFilteredCourses(mockCourses);
        await AsyncStorage.setItem('courseCatalog', JSON.stringify(mockCourses));
      }
    } catch (error) {
      console.error('Error loading courses:', error);
      const mockCourses = await generateMockCourses();
      setCourses(mockCourses);
      setFilteredCourses(mockCourses);
    }
  };

  const generateMockCourses = async () => {
    return [
      // Computer Engineering
      {
        id: 'C001',
        title: 'Introduction to Python for Engineers',
        subtitle: 'Programming fundamentals for engineering applications',
        instructor: 'Prof. Sarah Johnson',
        duration: '8 hours',
        totalLessons: 24,
        rating: 4.8,
        students: 18500,
        level: 'Beginner',
        isFree: true,
        price: 0,
        originalPrice: 49,
        color: '#3776AB',
        icon: 'language-python',
        category: 'computer',
        tags: ['Programming', 'Python', 'Engineering'],
        description: 'Learn Python programming fundamentals with engineering applications including data analysis, automation, and scientific computing. Perfect for beginners with no prior programming experience.',
        longDescription: 'This comprehensive course introduces Python programming from an engineering perspective. You\'ll learn how to use Python for data analysis, automation, scientific computing, and solving engineering problems. The course includes hands-on projects, real-world case studies, and practical exercises.',
        modules: [
          { id: 'M1', title: 'Python Basics and Syntax', duration: '2 hours', lessons: 8, completed: false },
          { id: 'M2', title: 'Data Structures for Engineering', duration: '2 hours', lessons: 6, completed: false },
          { id: 'M3', title: 'NumPy and Scientific Computing', duration: '2 hours', lessons: 5, completed: false },
          { id: 'M4', title: 'Engineering Applications', duration: '2 hours', lessons: 5, completed: false },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1526379879527-8559ecfcaec7?w=400',
        instructorPhoto: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=200',
        instructorBio: 'Professor of Computer Engineering with 15+ years of experience',
        startDate: '2024-03-01',
        endDate: '2024-05-01',
        certificateAvailable: true,
        assignments: 5,
        quizzes: 3,
        language: 'English',
        subtitles: ['English', 'Spanish', 'Arabic'],
        reviews: [
          { id: 1, user: 'Ahmed M.', rating: 5, comment: 'Excellent course for beginners!', date: '2024-01-15' },
          { id: 2, user: 'Maria S.', rating: 4, comment: 'Great content, could use more examples', date: '2024-01-10' },
        ],
        requirements: ['No programming experience required', 'Basic mathematics knowledge'],
        learningOutcomes: ['Python programming fundamentals', 'Data analysis skills', 'Engineering problem-solving'],
        isFeatured: true,
        isTrending: true,
        isNew: false,
        discount: 0,
        enrolled: true,
        progress: 0,
        weeklyHours: '2-3 hours',
        publishedDate: '2023-12-01',
        lastUpdated: '2024-01-15',
      },
      {
        id: 'C002',
        title: 'Advanced Structural Analysis',
        subtitle: 'Finite element methods and dynamic analysis',
        instructor: 'Dr. Robert Williams',
        duration: '25 hours',
        totalLessons: 48,
        rating: 4.9,
        students: 3200,
        level: 'Advanced',
        isFree: false,
        price: 149,
        originalPrice: 199,
        color: '#0e4e96',
        icon: 'bridge',
        category: 'civil',
        tags: ['Structural', 'Analysis', 'Civil'],
        description: 'Master advanced structural analysis techniques including finite element methods, dynamic analysis, and seismic design principles.',
        longDescription: 'This advanced course covers sophisticated structural analysis methods used in modern engineering. Learn finite element analysis, dynamic response evaluation, seismic design principles, and non-linear analysis techniques. Includes practical projects using industry-standard software.',
        modules: [
          { id: 'M1', title: 'FEM Fundamentals', duration: '5 hours', lessons: 12, completed: false },
          { id: 'M2', title: 'Dynamic Analysis', duration: '6 hours', lessons: 14, completed: false },
          { id: 'M3', title: 'Seismic Design Principles', duration: '7 hours', lessons: 16, completed: false },
          { id: 'M4', title: 'Advanced Applications', duration: '7 hours', lessons: 18, completed: false },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=400',
        instructorPhoto: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200',
        instructorBio: 'Structural Engineering Professor with PhD in Civil Engineering',
        startDate: '2024-02-15',
        endDate: '2024-06-15',
        certificateAvailable: true,
        assignments: 8,
        quizzes: 4,
        language: 'English',
        subtitles: ['English', 'French'],
        reviews: [
          { id: 1, user: 'Engineer J.', rating: 5, comment: 'Best structural course online!', date: '2024-01-20' },
        ],
        requirements: ['Basic structural analysis knowledge', 'Engineering degree recommended'],
        learningOutcomes: ['FEM mastery', 'Dynamic analysis skills', 'Seismic design expertise'],
        isFeatured: true,
        isTrending: false,
        isNew: true,
        discount: 25,
        enrolled: false,
        progress: 0,
        weeklyHours: '4-5 hours',
        publishedDate: '2024-01-01',
        lastUpdated: '2024-01-20',
      },
      {
        id: 'C003',
        title: 'Machine Learning for Engineers',
        subtitle: 'AI applications in engineering systems',
        instructor: 'Dr. Alex Rodriguez',
        duration: '30 hours',
        totalLessons: 36,
        rating: 4.8,
        students: 4500,
        level: 'Intermediate',
        isFree: false,
        price: 199,
        originalPrice: 299,
        color: '#FF4081',
        icon: 'robot',
        category: 'computer',
        tags: ['AI', 'Machine Learning', 'Data Science'],
        description: 'Apply machine learning techniques to solve complex engineering problems including predictive maintenance, quality control, and optimization.',
        longDescription: 'Learn how to implement machine learning algorithms in engineering contexts. This course covers data preprocessing, model selection, neural networks, and real-world engineering applications. Includes hands-on projects with Python and TensorFlow.',
        modules: [
          { id: 'M1', title: 'ML Fundamentals for Engineers', duration: '6 hours', lessons: 9, completed: false },
          { id: 'M2', title: 'Data Preprocessing', duration: '6 hours', lessons: 8, completed: false },
          { id: 'M3', title: 'Predictive Modeling', duration: '9 hours', lessons: 12, completed: false },
          { id: 'M4', title: 'Engineering Case Studies', duration: '9 hours', lessons: 10, completed: false },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400',
        instructorPhoto: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200',
        instructorBio: 'AI Researcher with focus on Engineering Applications',
        startDate: '2024-03-10',
        endDate: '2024-07-10',
        certificateAvailable: true,
        assignments: 6,
        quizzes: 3,
        language: 'English',
        subtitles: ['English', 'Chinese'],
        reviews: [],
        requirements: ['Python programming basics', 'Linear algebra knowledge'],
        learningOutcomes: ['ML algorithm implementation', 'Engineering problem-solving with AI', 'Model deployment'],
        isFeatured: false,
        isTrending: true,
        isNew: false,
        discount: 33,
        enrolled: false,
        progress: 0,
        weeklyHours: '5-6 hours',
        publishedDate: '2023-11-01',
        lastUpdated: '2024-01-10',
      },
      {
        id: 'C004',
        title: 'Electrical Circuit Design',
        subtitle: 'Digital and analog circuit fundamentals',
        instructor: 'Prof. Michael Chen',
        duration: '15 hours',
        totalLessons: 30,
        rating: 4.7,
        students: 2800,
        level: 'Intermediate',
        isFree: true,
        price: 0,
        originalPrice: 99,
        color: '#FFD700',
        icon: 'flash',
        category: 'electrical',
        tags: ['Circuits', 'Electronics', 'Design'],
        description: 'Comprehensive course on electrical circuit design covering both digital and analog circuits with practical lab exercises.',
        longDescription: 'Master electrical circuit design from basics to advanced topics. Learn analog circuit analysis, digital logic design, PCB layout, and circuit simulation. Includes virtual lab exercises and design projects.',
        modules: [
          { id: 'M1', title: 'Circuit Fundamentals', duration: '4 hours', lessons: 8, completed: false },
          { id: 'M2', title: 'Analog Circuits', duration: '5 hours', lessons: 10, completed: false },
          { id: 'M3', title: 'Digital Circuits', duration: '3 hours', lessons: 6, completed: false },
          { id: 'M4', title: 'PCB Design', duration: '3 hours', lessons: 6, completed: false },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=400',
        instructorPhoto: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=200',
        instructorBio: 'Electrical Engineering Professor with Industry Experience',
        startDate: '2024-02-01',
        endDate: '2024-04-01',
        certificateAvailable: false,
        assignments: 4,
        quizzes: 2,
        language: 'English',
        subtitles: ['English'],
        reviews: [
          { id: 1, user: 'Student E.', rating: 4, comment: 'Good content, needs more examples', date: '2024-01-05' },
        ],
        requirements: ['Basic physics knowledge', 'High school mathematics'],
        learningOutcomes: ['Circuit analysis skills', 'PCB design fundamentals', 'Troubleshooting techniques'],
        isFeatured: false,
        isTrending: false,
        isNew: true,
        discount: 100,
        enrolled: false,
        progress: 0,
        weeklyHours: '3-4 hours',
        publishedDate: '2024-01-15',
        lastUpdated: '2024-01-15',
      },
      {
        id: 'C005',
        title: 'Chemical Process Engineering',
        subtitle: 'Industrial chemical processes and operations',
        instructor: 'Dr. Emily Watson',
        duration: '20 hours',
        totalLessons: 40,
        rating: 4.6,
        students: 2100,
        level: 'Intermediate',
        isFree: false,
        price: 179,
        originalPrice: 249,
        color: '#4CAF50',
        icon: 'flask',
        category: 'chemical',
        tags: ['Chemical', 'Process', 'Industrial'],
        description: 'Learn chemical process engineering principles, equipment design, and industrial operations.',
        longDescription: 'This course covers chemical process fundamentals, equipment design, safety protocols, and industrial operations. Learn process optimization, equipment selection, and chemical plant design principles.',
        modules: [
          { id: 'M1', title: 'Process Fundamentals', duration: '5 hours', lessons: 10, completed: false },
          { id: 'M2', title: 'Equipment Design', duration: '6 hours', lessons: 12, completed: false },
          { id: 'M3', title: 'Process Control', duration: '5 hours', lessons: 10, completed: false },
          { id: 'M4', title: 'Industrial Applications', duration: '4 hours', lessons: 8, completed: false },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
        instructorPhoto: 'https://images.unsplash.com/photo-1544725176-7c40e5a71c5e?w=200',
        instructorBio: 'Chemical Engineering Professor with Industry Background',
        startDate: '2024-03-15',
        endDate: '2024-06-15',
        certificateAvailable: true,
        assignments: 6,
        quizzes: 3,
        language: 'English',
        subtitles: ['English', 'German'],
        reviews: [],
        requirements: ['Chemistry basics', 'Engineering fundamentals'],
        learningOutcomes: ['Process design skills', 'Equipment selection knowledge', 'Safety protocols'],
        isFeatured: true,
        isTrending: false,
        isNew: false,
        discount: 28,
        enrolled: false,
        progress: 0,
        weeklyHours: '4-5 hours',
        publishedDate: '2023-10-01',
        lastUpdated: '2024-01-05',
      },
      {
        id: 'C006',
        title: 'Mechanical CAD & SolidWorks',
        subtitle: '3D modeling and mechanical design',
        instructor: 'Eng. David Miller',
        duration: '18 hours',
        totalLessons: 36,
        rating: 4.9,
        students: 5200,
        level: 'Beginner',
        isFree: false,
        price: 129,
        originalPrice: 179,
        color: '#FF6B35',
        icon: 'cog',
        category: 'mechanical',
        tags: ['CAD', 'SolidWorks', '3D Modeling'],
        description: 'Master 3D modeling, mechanical design, and engineering drawings using SolidWorks.',
        longDescription: 'Learn professional 3D modeling and mechanical design using SolidWorks. This course covers sketching, part modeling, assembly design, engineering drawings, and simulations. Perfect for aspiring mechanical engineers and designers.',
        modules: [
          { id: 'M1', title: 'SolidWorks Basics', duration: '4 hours', lessons: 8, completed: false },
          { id: 'M2', title: 'Part Modeling', duration: '5 hours', lessons: 10, completed: false },
          { id: 'M3', title: 'Assembly Design', duration: '5 hours', lessons: 10, completed: false },
          { id: 'M4', title: 'Engineering Drawings', duration: '4 hours', lessons: 8, completed: false },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
        instructorPhoto: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200',
        instructorBio: 'Mechanical Engineer and Certified SolidWorks Expert',
        startDate: '2024-02-20',
        endDate: '2024-05-20',
        certificateAvailable: true,
        assignments: 5,
        quizzes: 2,
        language: 'English',
        subtitles: ['English', 'Spanish', 'Portuguese'],
        reviews: [
          { id: 1, user: 'Designer M.', rating: 5, comment: 'Excellent SolidWorks course!', date: '2024-01-12' },
          { id: 2, user: 'Student R.', rating: 5, comment: 'Very comprehensive and well-taught', date: '2024-01-08' },
        ],
        requirements: ['Basic computer skills', 'No prior CAD experience needed'],
        learningOutcomes: ['Professional 3D modeling', 'Assembly design skills', 'Engineering drawings'],
        isFeatured: false,
        isTrending: true,
        isNew: false,
        discount: 28,
        enrolled: false,
        progress: 0,
        weeklyHours: '3-4 hours',
        publishedDate: '2023-09-15',
        lastUpdated: '2024-01-18',
      },
      {
        id: 'C007',
        title: 'Renewable Energy Systems',
        subtitle: 'Solar, wind, and sustainable energy technologies',
        instructor: 'Dr. Lisa Thompson',
        duration: '22 hours',
        totalLessons: 44,
        rating: 4.7,
        students: 3800,
        level: 'Intermediate',
        isFree: false,
        price: 169,
        originalPrice: 229,
        color: '#4ECDC4',
        icon: 'solar-panel',
        category: 'electrical',
        tags: ['Renewable', 'Energy', 'Sustainability'],
        description: 'Comprehensive guide to renewable energy systems design and implementation.',
        longDescription: 'Learn about solar PV systems, wind energy, hydro power, and energy storage technologies. This course covers system design, installation, maintenance, and economic analysis of renewable energy projects.',
        modules: [
          { id: 'M1', title: 'Solar Energy Systems', duration: '6 hours', lessons: 12, completed: false },
          { id: 'M2', title: 'Wind Energy Technology', duration: '6 hours', lessons: 12, completed: false },
          { id: 'M3', title: 'Energy Storage', duration: '5 hours', lessons: 10, completed: false },
          { id: 'M4', title: 'System Design & Economics', duration: '5 hours', lessons: 10, completed: false },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=400',
        instructorPhoto: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=200',
        instructorBio: 'Renewable Energy Researcher and Consultant',
        startDate: '2024-03-05',
        endDate: '2024-07-05',
        certificateAvailable: true,
        assignments: 7,
        quizzes: 3,
        language: 'English',
        subtitles: ['English', 'French'],
        reviews: [
          { id: 1, user: 'Energy E.', rating: 4, comment: 'Great course on renewable tech', date: '2024-01-14' },
        ],
        requirements: ['Basic engineering concepts', 'High school physics'],
        learningOutcomes: ['Renewable system design', 'Energy analysis skills', 'Project evaluation'],
        isFeatured: true,
        isTrending: false,
        isNew: true,
        discount: 26,
        enrolled: false,
        progress: 0,
        weeklyHours: '4-5 hours',
        publishedDate: '2024-01-10',
        lastUpdated: '2024-01-10',
      },
      {
        id: 'C008',
        title: 'Engineering Mathematics',
        subtitle: 'Calculus, linear algebra, and differential equations',
        instructor: 'Dr. James Wilson',
        duration: '30 hours',
        totalLessons: 60,
        rating: 4.8,
        students: 12500,
        level: 'Beginner',
        isFree: true,
        price: 0,
        originalPrice: 149,
        color: '#9B30FF',
        icon: 'calculator',
        category: 'mathematics',
        tags: ['Mathematics', 'Calculus', 'Engineering Math'],
        description: 'Essential mathematics for engineering students and professionals.',
        longDescription: 'Master the mathematical foundations needed for engineering. This comprehensive course covers calculus, linear algebra, differential equations, and numerical methods with engineering applications.',
        modules: [
          { id: 'M1', title: 'Calculus Fundamentals', duration: '8 hours', lessons: 16, completed: false },
          { id: 'M2', title: 'Linear Algebra', duration: '7 hours', lessons: 14, completed: false },
          { id: 'M3', title: 'Differential Equations', duration: '8 hours', lessons: 16, completed: false },
          { id: 'M4', title: 'Numerical Methods', duration: '7 hours', lessons: 14, completed: false },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1509228468518-180dd4864904?w=400',
        instructorPhoto: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200',
        instructorBio: 'Mathematics Professor specializing in Engineering Applications',
        startDate: '2024-02-01',
        endDate: '2024-06-01',
        certificateAvailable: true,
        assignments: 8,
        quizzes: 4,
        language: 'English',
        subtitles: ['English', 'Arabic', 'Spanish'],
        reviews: [
          { id: 1, user: 'Student A.', rating: 5, comment: 'Saved my engineering degree!', date: '2024-01-03' },
          { id: 2, user: 'Engineer K.', rating: 5, comment: 'Excellent mathematical foundation', date: '2024-01-07' },
        ],
        requirements: ['High school mathematics', 'Basic algebra knowledge'],
        learningOutcomes: ['Calculus mastery', 'Linear algebra skills', 'Differential equations solving'],
        isFeatured: false,
        isTrending: true,
        isNew: false,
        discount: 100,
        enrolled: false,
        progress: 0,
        weeklyHours: '5-6 hours',
        publishedDate: '2023-08-01',
        lastUpdated: '2024-01-12',
      },
    ];
  };

  const loadBookmarks = async () => {
    try {
      const bookmarks = await AsyncStorage.getItem('bookmarkedCourses') || '[]';
      setBookmarkedCourses(JSON.parse(bookmarks));
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }
  };

  const loadEnrolledCourses = async () => {
    try {
      const enrolled = await AsyncStorage.getItem('enrolledCourses') || '[]';
      setEnrolledCourses(JSON.parse(enrolled));
    } catch (error) {
      console.error('Error loading enrolled courses:', error);
    }
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadInitialData();
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const filterCourses = () => {
    let filtered = [...courses];
    
    // Apply active tab filter
    switch (activeTab) {
      case 'trending':
        filtered = filtered.filter(course => course.isTrending);
        break;
      case 'new':
        filtered = filtered.filter(course => course.isNew);
        break;
      case 'recommended':
        // In real app, this would use ML recommendations
        filtered = filtered.filter(course => course.rating >= 4.5);
        break;
      case 'free':
        filtered = filtered.filter(course => course.isFree);
        break;
      default:
        // 'all' - keep all courses
        break;
    }
    
    // Apply category filter
    if (selectedFilters.category !== 'all') {
      filtered = filtered.filter(course => course.category === selectedFilters.category);
    }
    
    // Apply level filter
    if (selectedFilters.level !== 'all') {
      filtered = filtered.filter(course => course.level === selectedFilters.level);
    }
    
    // Apply price filter
    if (selectedFilters.price !== 'all') {
      if (selectedFilters.price === 'free') {
        filtered = filtered.filter(course => course.isFree);
      } else if (selectedFilters.price === 'paid') {
        filtered = filtered.filter(course => !course.isFree);
      } else if (selectedFilters.price === 'discounted') {
        filtered = filtered.filter(course => course.discount > 0);
      }
    }
    
    // Apply rating filter
    if (selectedFilters.rating > 0) {
      filtered = filtered.filter(course => course.rating >= selectedFilters.rating);
    }
    
    // Apply duration filter
    if (selectedFilters.duration !== 'all') {
      const durationMap = {
        'short': (d) => parseInt(d) <= 10,
        'medium': (d) => parseInt(d) > 10 && parseInt(d) <= 20,
        'long': (d) => parseInt(d) > 20,
      };
      if (durationMap[selectedFilters.duration]) {
        filtered = filtered.filter(course => 
          durationMap[selectedFilters.duration](course.duration)
        );
      }
    }
    
    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(course =>
        String(course.title ?? '').toLowerCase().includes(query) ||
        String(course.subtitle ?? '').toLowerCase().includes(query) ||
        String(course.instructor ?? '').toLowerCase().includes(query) ||
        course.tags?.some(tag => tag.toLowerCase().includes(query)) ||
        String(course.description ?? '').toLowerCase().includes(query)
      );
    }
    
    setFilteredCourses(filtered);
  };

  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      filterCourses();
    }, 300);
  };

  const toggleBookmark = async (courseId) => {
    try {
      let newBookmarks;
      if (bookmarkedCourses.includes(courseId)) {
        newBookmarks = bookmarkedCourses.filter(id => id !== courseId);
      } else {
        newBookmarks = [...bookmarkedCourses, courseId];
      }
      
      setBookmarkedCourses(newBookmarks);
      await AsyncStorage.setItem('bookmarkedCourses', JSON.stringify(newBookmarks));
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const enrollInCourse = async (course) => {
    try {
      // Check if already enrolled
      if (enrolledCourses.some(c => c.id === course.id)) {
        Alert.alert('Already Enrolled', 'You are already enrolled in this course.');
        return;
      }
      
      // For paid courses, show payment option
      if (!course.isFree) {
        Alert.alert(
          'Enroll in Course',
          `This course costs $${course.price}. Do you want to proceed?`,
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Enroll',
              style: 'default',
              onPress: () => completeEnrollment(course),
            },
          ]
        );
      } else {
        await completeEnrollment(course);
      }
    } catch (error) {
      console.error('Error enrolling in course:', error);
      Alert.alert('Error', 'Failed to enroll in course');
    }
  };

  const completeEnrollment = async (course) => {
    try {
      const enrolledCourse = {
        ...course,
        enrolled: true,
        progress: 0,
        completedLessons: 0,
        startDate: new Date().toISOString().split('T')[0],
        lastAccessed: new Date().toISOString().split('T')[0],
        timeSpent: '0h 0m',
        completedAssignments: 0,
        completedQuizzes: 0,
        discussionPosts: 0,
      };
      
      const updatedEnrolled = [...enrolledCourses, enrolledCourse];
      setEnrolledCourses(updatedEnrolled);
      await AsyncStorage.setItem('enrolledCourses', JSON.stringify(updatedEnrolled));
      
      // Update course catalog
      const updatedCourses = courses.map(c => 
        c.id === course.id ? { ...c, enrolled: true } : c
      );
      setCourses(updatedCourses);
      await AsyncStorage.setItem('courseCatalog', JSON.stringify(updatedCourses));
      
      Alert.alert(
        'Success!',
        `You have successfully enrolled in "${course.title}"`,
        [
          { text: 'Start Learning', onPress: () => navigation.navigate('MyCourses') },
          { text: 'Continue Browsing', style: 'cancel' },
        ]
      );
      
    } catch (error) {
      console.error('Error completing enrollment:', error);
      throw error;
    }
  };

  const playVideoPreview = (videoUrl) => {
    setSelectedPreviewVideo(videoUrl);
    setVideoModalVisible(true);
  };

  const resetFilters = () => {
    setSelectedFilters({
      category: 'all',
      level: 'all',
      price: 'all',
      rating: 0,
      duration: 'all',
    });
    setSearchQuery('');
    setActiveTab('all');
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'computer': return 'laptop';
      case 'civil': return 'bridge';
      case 'mechanical': return 'cog';
      case 'electrical': return 'flash';
      case 'chemical': return 'flask';
      case 'mathematics': return 'calculator';
      default: return 'book-open-variant';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'computer': return '#3776AB';
      case 'civil': return '#0e4e96';
      case 'mechanical': return '#FF6B35';
      case 'electrical': return '#FFD700';
      case 'chemical': return '#4CAF50';
      case 'mathematics': return '#9B30FF';
      default: return '#667eea';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return '#10b981';
      case 'Intermediate': return '#f59e0b';
      case 'Advanced': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const categories = [
    { id: 'all', label: 'All Categories', icon: 'grid' },
    { id: 'computer', label: 'Computer', icon: 'laptop' },
    { id: 'civil', label: 'Civil', icon: 'bridge' },
    { id: 'mechanical', label: 'Mechanical', icon: 'cog' },
    { id: 'electrical', label: 'Electrical', icon: 'flash' },
    { id: 'chemical', label: 'Chemical', icon: 'flask' },
    { id: 'mathematics', label: 'Mathematics', icon: 'calculator' },
  ];

  const levels = [
    { id: 'all', label: 'All Levels' },
    { id: 'Beginner', label: 'Beginner' },
    { id: 'Intermediate', label: 'Intermediate' },
    { id: 'Advanced', label: 'Advanced' },
  ];

  const prices = [
    { id: 'all', label: 'All Prices' },
    { id: 'free', label: 'Free Only' },
    { id: 'paid', label: 'Paid Only' },
    { id: 'discounted', label: 'Discounted' },
  ];

  const durations = [
    { id: 'all', label: 'Any Duration' },
    { id: 'short', label: 'Short (< 10h)' },
    { id: 'medium', label: 'Medium (10-20h)' },
    { id: 'long', label: 'Long (> 20h)' },
  ];

  const ratings = [4.5, 4.0, 3.5, 3.0];

  const tabs = [
    { id: 'all', label: 'All Courses', icon: 'grid' },
    { id: 'trending', label: 'Trending', icon: 'trending-up' },
    { id: 'new', label: 'New', icon: 'new-box' },
    { id: 'recommended', label: 'Recommended', icon: 'star' },
    { id: 'free', label: 'Free', icon: 'currency-usd-off' },
  ];

  const renderLoading = () => (
    <View style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <ActivityIndicator size={36} color="#667eea" />
      <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#1f2937' }]}>
        Loading Course Catalog...
      </Text>
    </View>
  );

  const renderHeader = () => (
    <Animated.View 
      style={[
        styles.header,
        {
          height: headerHeight,
          opacity: headerOpacity,
        },
      ]}
    >
      <LinearGradient
        colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
        style={styles.headerGradient}
      >
        <View style={styles.headerTop}>
          <TouchableOpacity 
            style={styles.menuButton}
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
          </TouchableOpacity>
          
          <Text style={styles.headerTitle}>📚 Course Catalog</Text>
          
          <TouchableOpacity 
            style={styles.iconButton}
            onPress={() => navigation.navigate('MyCourses')}
          >
            <MaterialCommunityIcons name="book-open-variant" size={24} color="#ffffff" />
          </TouchableOpacity>
        </View>
        
        <Text style={styles.headerSubtitle}>
          Discover engineering courses to advance your career
        </Text>
        
        <View style={styles.statsContainer}>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>{courses.length}</Text>
            <Text style={styles.statLabel}>Courses</Text>
          </View>
          
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>
              {courses.filter(c => c.isFree).length}
            </Text>
            <Text style={styles.statLabel}>Free</Text>
          </View>
          
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>
              {courses.reduce((sum, c) => sum + c.students, 0).toLocaleString()}
            </Text>
            <Text style={styles.statLabel}>Students</Text>
          </View>
          
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>
              {Math.max(...courses.map(c => c.rating), 0).toFixed(1)}
            </Text>
            <Text style={styles.statLabel}>Avg Rating</Text>
          </View>
        </View>
      </LinearGradient>
    </Animated.View>
  );

  const renderSearchBar = () => (
    <View style={[styles.searchContainer, { 
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      shadowColor: darkMode ? '#000' : '#667eea',
    }]}>
      <View style={styles.searchInputContainer}>
        <MaterialCommunityIcons 
          name="magnify" 
          size={20} 
          color={darkMode ? '#a0aec0' : '#6b7280'} 
        />
        <TextInput
          style={[styles.searchInput, { color: darkMode ? '#f7fafc' : '#1f2937' }]}
          placeholder="Search courses, instructors, topics..."
          placeholderTextColor={darkMode ? '#a0aec0' : '#6b7280'}
          value={searchQuery}
          onChangeText={handleSearch}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons 
              name="close-circle" 
              size={18} 
              color={darkMode ? '#a0aec0' : '#6b7280'} 
            />
          </TouchableOpacity>
        )}
      </View>
      
      <TouchableOpacity
        style={[styles.filterButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
        onPress={() => setShowFilters(true)}
      >
        <MaterialCommunityIcons 
          name="filter-variant" 
          size={18} 
          color={darkMode ? '#ffffff' : '#1f2937'} 
        />
        {Object.values(selectedFilters).some(val => val !== 'all' && val !== 0) && (
          <View style={styles.filterBadge}>
            <Text style={styles.filterBadgeText}>!</Text>
          </View>
        )}
      </TouchableOpacity>
    </View>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[
              styles.tabButton,
              { backgroundColor: darkMode ? '#2d3748' : '#ffffff' },
              activeTab === tab.id && { backgroundColor: '#667eea' }
            ]}
            onPress={() => setActiveTab(tab.id)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={18} 
              color={activeTab === tab.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
            />
            <Text style={[
              styles.tabText,
              { color: activeTab === tab.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
            ]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderCategoryFilters = () => (
    <View style={styles.categoryFilters}>
      <Text style={[styles.categoryTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
        Browse by Category:
      </Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {categories.map((category) => (
          <TouchableOpacity
            key={category.id}
            style={[
              styles.categoryButton,
              { backgroundColor: darkMode ? '#2d3748' : '#ffffff' },
              selectedFilters.category === category.id && { 
                backgroundColor: getCategoryColor(category.id),
              }
            ]}
            onPress={() => setSelectedFilters({...selectedFilters, category: category.id})}
          >
            <MaterialCommunityIcons 
              name={category.icon} 
              size={16} 
              color={selectedFilters.category === category.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
            />
            <Text style={[
              styles.categoryButtonText,
              { color: selectedFilters.category === category.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
            ]}>
              {category.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderCourseCard = (course) => (
    <Animated.View 
      style={[
        styles.courseCard,
        {
          opacity: fadeAnim,
          transform: [{
            translateY: slideAnim,
          }],
        },
      ]}
      key={course.id}
    >
      <TouchableOpacity
        style={[styles.courseCardContent, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
        onPress={() => {
          setSelectedCourse(course);
          setShowCourseDetails(true);
        }}
        activeOpacity={0.9}
      >
        {/* Course Image/Thumbnail */}
        <View style={styles.courseImageContainer}>
          <Image 
            source={{ uri: (course.thumbnail && typeof course.thumbnail=="string" && course.thumbnail.startsWith("http")) ? course.thumbnail : undefined }} 
            style={styles.courseImage}
            resizeMode="cover"
          />
          
          {/* Course Badges */}
          <View style={styles.courseBadges}>
            {course.isFree && (
              <View style={[styles.badge, styles.freeBadge]}>
                <Text style={styles.badgeText}>FREE</Text>
              </View>
            )}
            {course.discount > 0 && !course.isFree && (
              <View style={[styles.badge, styles.discountBadge]}>
                <Text style={styles.badgeText}>{course.discount}% OFF</Text>
              </View>
            )}
            {course.isNew && (
              <View style={[styles.badge, styles.newBadge]}>
                <Text style={styles.badgeText}>NEW</Text>
              </View>
            )}
            {course.isFeatured && (
              <View style={[styles.badge, styles.featuredBadge]}>
                <Text style={styles.badgeText}>FEATURED</Text>
              </View>
            )}
          </View>
          
          {/* Preview Button */}
          <TouchableOpacity 
            style={styles.previewButton}
            onPress={(e) => {
              e.stopPropagation();
              playVideoPreview(course.videoPreview);
            }}
          >
            <MaterialCommunityIcons name="play-circle" size={40} color="#ffffff" />
          </TouchableOpacity>
        </View>
        
        <View style={styles.courseContent}>
          {/* Course Header */}
          <View style={styles.courseHeader}>
            <LinearGradient
              colors={[course.color, `${course.color}80`]}
              style={styles.courseIcon}
            >
              <MaterialCommunityIcons name={course.icon} size={20} color="#ffffff" />
            </LinearGradient>
            
            <View style={styles.courseTitleContainer}>
              <Text style={[styles.courseTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]} numberOfLines={2}>
                {course.title}
              </Text>
              <Text style={[styles.courseInstructor, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {course.instructor}
              </Text>
            </View>
            
            <TouchableOpacity 
              style={styles.bookmarkButton}
              onPress={(e) => {
                e.stopPropagation();
                toggleBookmark(course.id);
              }}
            >
              <MaterialCommunityIcons
                name={bookmarkedCourses.includes(course.id) ? "bookmark" : "bookmark-outline"}
                size={20}
                color={bookmarkedCourses.includes(course.id) ? "#f59e0b" : (darkMode ? "#a0aec0" : "#6b7280")}
              />
            </TouchableOpacity>
          </View>
          
          {/* Course Subtitle */}
          <Text style={[styles.courseSubtitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]} numberOfLines={2}>
            {course.subtitle}
          </Text>
          
          {/* Course Details */}
          <View style={styles.courseDetails}>
            <View style={styles.detailRow}>
              <View style={styles.detailItem}>
                <MaterialCommunityIcons name="clock-outline" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
                <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {course.duration}
                </Text>
              </View>
              
              <View style={styles.detailItem}>
                <MaterialCommunityIcons name="play-circle" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
                <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {course.totalLessons} lessons
                </Text>
              </View>
              
              <View style={styles.detailItem}>
                <MaterialCommunityIcons name="account-group" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
                <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {course.students.toLocaleString()}
                </Text>
              </View>
            </View>
            
            <View style={styles.detailRow}>
              <View style={styles.detailItem}>
                <View style={[styles.levelBadge, { backgroundColor: getLevelColor(course.level) + '20' }]}>
                  <Text style={[styles.levelText, { color: getLevelColor(course.level) }]}>
                    {course.level}
                  </Text>
                </View>
              </View>
              
              <View style={styles.detailItem}>
                <MaterialCommunityIcons name="star" size={14} color="#fbbf24" />
                <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {course.rating.toFixed(1)}
                </Text>
              </View>
              
              <View style={styles.detailItem}>
                <MaterialCommunityIcons name="certificate" size={14} color={course.certificateAvailable ? "#10b981" : (darkMode ? '#a0aec0' : '#6b7280')} />
                <Text style={[styles.detailText, { 
                  color: course.certificateAvailable ? "#10b981" : (darkMode ? '#a0aec0' : '#6b7280')
                }]}>
                  {course.certificateAvailable ? 'Certificate' : 'No Cert'}
                </Text>
              </View>
            </View>
          </View>
          
          {/* Course Tags */}
          {course.tags && course.tags.length > 0 && (
            <View style={styles.courseTags}>
              {course.tags.slice(0, 3).map((tag, index) => (
                <View 
                  key={index} 
                  style={[styles.tag, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
                >
                  <Text style={[styles.tagText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>{tag}</Text>
                </View>
              ))}
            </View>
          )}
          
          {/* Course Price and Action */}
          <View style={styles.courseFooter}>
            <View style={styles.priceContainer}>
              {course.isFree ? (
                <Text style={[styles.freePrice, { color: '#10b981' }]}>Free</Text>
              ) : (
                <>
                  <Text style={[styles.currentPrice, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    ${course.price}
                  </Text>
                  {course.originalPrice > course.price && (
                    <Text style={[styles.originalPrice, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      ${course.originalPrice}
                    </Text>
                  )}
                </>
              )}
            </View>
            
            {course.enrolled ? (
              <TouchableOpacity
                style={[styles.actionButton, styles.continueButton]}
                onPress={(e) => {
                  e.stopPropagation();
                  navigation.navigate('CourseContent', { course });
                }}
              >
                <MaterialCommunityIcons name="play-circle" size={16} color="#ffffff" />
                <Text style={styles.continueButtonText}>Continue</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[
                  styles.actionButton, 
                  course.isFree ? styles.enrollFreeButton : styles.enrollPaidButton
                ]}
                onPress={(e) => {
                  e.stopPropagation();
                  enrollInCourse(course);
                }}
              >
                <MaterialCommunityIcons 
                  name={course.isFree ? "download" : "cart-plus"} 
                  size={16} 
                  color="#ffffff" 
                />
                <Text style={styles.enrollButtonText}>
                  {course.isFree ? 'Enroll Free' : 'Enroll Now'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </TouchableOpacity>
    </Animated.View>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name="magnify" size={64} color={darkMode ? '#4a5568' : '#d1d5db'} />
      <Text style={[styles.emptyStateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        No Courses Found
      </Text>
      <Text style={[styles.emptyStateText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {searchQuery 
          ? 'Try adjusting your search or filters'
          : 'Check back later for new courses'}
      </Text>
      
      <TouchableOpacity
        style={styles.emptyStateButton}
        onPress={resetFilters}
      >
        <MaterialCommunityIcons name="filter-remove" size={20} color="#ffffff" />
        <Text style={styles.emptyStateButtonText}>Reset Filters</Text>
      </TouchableOpacity>
    </View>
  );

  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowFilters(false)}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setShowFilters(false)}
      >
        <View style={[styles.filtersModal, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.filtersHeader}>
            <Text style={[styles.filtersTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              🔍 Filter Courses
            </Text>
            <TouchableOpacity onPress={() => setShowFilters(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.filtersContent} showsVerticalScrollIndicator={false}>
            {/* Category Filter */}
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Category
              </Text>
              <View style={styles.filterOptions}>
                {categories.map((category) => (
                  <TouchableOpacity
                    key={category.id}
                    style={[
                      styles.filterOption,
                      { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' },
                      selectedFilters.category === category.id && { 
                        backgroundColor: getCategoryColor(category.id),
                      }
                    ]}
                    onPress={() => setSelectedFilters({...selectedFilters, category: category.id})}
                  >
                    <MaterialCommunityIcons 
                      name={category.icon} 
                      size={16} 
                      color={selectedFilters.category === category.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                    />
                    <Text style={[
                      styles.filterOptionText,
                      { color: selectedFilters.category === category.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
                    ]}>
                      {category.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            {/* Level Filter */}
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Level
              </Text>
              <View style={styles.filterOptions}>
                {levels.map((level) => (
                  <TouchableOpacity
                    key={level.id}
                    style={[
                      styles.filterOption,
                      { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' },
                      selectedFilters.level === level.id && { 
                        backgroundColor: getLevelColor(level.id),
                      }
                    ]}
                    onPress={() => setSelectedFilters({...selectedFilters, level: level.id})}
                  >
                    <Text style={[
                      styles.filterOptionText,
                      { color: selectedFilters.level === level.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
                    ]}>
                      {level.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            {/* Price Filter */}
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Price
              </Text>
              <View style={styles.filterOptions}>
                {prices.map((price) => (
                  <TouchableOpacity
                    key={price.id}
                    style={[
                      styles.filterOption,
                      { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' },
                      selectedFilters.price === price.id && { 
                        backgroundColor: '#667eea',
                      }
                    ]}
                    onPress={() => setSelectedFilters({...selectedFilters, price: price.id})}
                  >
                    <Text style={[
                      styles.filterOptionText,
                      { color: selectedFilters.price === price.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
                    ]}>
                      {price.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            {/* Rating Filter */}
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Minimum Rating
              </Text>
              <View style={styles.ratingFilter}>
                {ratings.map((rating) => (
                  <TouchableOpacity
                    key={rating}
                    style={[
                      styles.ratingOption,
                      { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' },
                      selectedFilters.rating === rating && { 
                        backgroundColor: '#fbbf24',
                      }
                    ]}
                    onPress={() => setSelectedFilters({...selectedFilters, rating})}
                  >
                    <MaterialCommunityIcons 
                      name="star" 
                      size={16} 
                      color={selectedFilters.rating === rating ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                    />
                    <Text style={[
                      styles.ratingText,
                      { color: selectedFilters.rating === rating ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
                    ]}>
                      {rating}+
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            {/* Duration Filter */}
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Duration
              </Text>
              <View style={styles.filterOptions}>
                {durations.map((duration) => (
                  <TouchableOpacity
                    key={duration.id}
                    style={[
                      styles.filterOption,
                      { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' },
                      selectedFilters.duration === duration.id && { 
                        backgroundColor: '#667eea',
                      }
                    ]}
                    onPress={() => setSelectedFilters({...selectedFilters, duration: duration.id})}
                  >
                    <Text style={[
                      styles.filterOptionText,
                      { color: selectedFilters.duration === duration.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
                    ]}>
                      {duration.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </ScrollView>
          
          <View style={[styles.filtersActions, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <TouchableOpacity
              style={[styles.filterActionButton, styles.resetButton]}
              onPress={resetFilters}
            >
              <MaterialCommunityIcons name="filter-remove" size={20} color="#ef4444" />
              <Text style={styles.resetButtonText}>Reset All</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.filterActionButton, styles.applyButton]}
              onPress={() => {
                setShowFilters(false);
                filterCourses();
              }}
            >
              <MaterialCommunityIcons name="check" size={20} color="#ffffff" />
              <Text style={styles.applyButtonText}>Apply Filters</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const renderCourseDetailsModal = () => (
    <Modal
      visible={showCourseDetails}
      animationType="slide"
      onRequestClose={() => setShowCourseDetails(false)}
    >
      {selectedCourse && (
        <SafeAreaView style={[styles.modalContainer, { backgroundColor: darkMode ? '#1a202c' : '#ffffff' }]}>
          <View style={[styles.modalHeader, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <TouchableOpacity onPress={() => setShowCourseDetails(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
            
            <Text style={[styles.modalTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]} numberOfLines={1}>
              Course Details
            </Text>
            
            <TouchableOpacity onPress={() => toggleBookmark(selectedCourse.id)}>
              <MaterialCommunityIcons
                name={bookmarkedCourses.includes(selectedCourse.id) ? "bookmark" : "bookmark-outline"}
                size={24}
                color={darkMode ? '#ffffff' : '#1f2937'}
              />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
            {/* Course Hero Section */}
            <View style={styles.courseHero}>
              <Image 
                source={{ uri: (selectedCourse.thumbnail && typeof selectedCourse.thumbnail=="string" && selectedCourse.thumbnail.startsWith("http")) ? selectedCourse.thumbnail : undefined }} 
                style={styles.courseHeroImage}
                resizeMode="cover"
              />
              <LinearGradient
                colors={['transparent', darkMode ? '#1a202c' : '#ffffff']}
                style={styles.courseHeroGradient}
              />
              
              <View style={styles.courseHeroContent}>
                <View style={styles.courseHeroHeader}>
                  <LinearGradient
                    colors={[selectedCourse.color, `${selectedCourse.color}80`]}
                    style={styles.courseHeroIcon}
                  >
                    <MaterialCommunityIcons name={selectedCourse.icon} size={32} color="#ffffff" />
                  </LinearGradient>
                  
                  <View style={styles.courseHeroInfo}>
                    <Text style={[styles.courseHeroTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {selectedCourse.title}
                    </Text>
                    <Text style={[styles.courseHeroInstructor, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      By {selectedCourse.instructor}
                    </Text>
                  </View>
                </View>
                
                <View style={styles.courseHeroStats}>
                  <View style={styles.heroStat}>
                    <MaterialCommunityIcons name="star" size={20} color="#fbbf24" />
                    <Text style={[styles.heroStatText, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {selectedCourse.rating.toFixed(1)}
                    </Text>
                  </View>
                  
                  <View style={styles.heroStat}>
                    <MaterialCommunityIcons name="account-group" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                    <Text style={[styles.heroStatText, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {selectedCourse.students.toLocaleString()} students
                    </Text>
                  </View>
                  
                  <View style={styles.heroStat}>
                    <MaterialCommunityIcons name="clock-outline" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                    <Text style={[styles.heroStatText, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {selectedCourse.duration}
                    </Text>
                  </View>
                </View>
                
                <TouchableOpacity
                  style={styles.previewHeroButton}
                  onPress={() => playVideoPreview(selectedCourse.videoPreview)}
                >
                  <MaterialCommunityIcons name="play-circle" size={24} color="#ffffff" />
                  <Text style={styles.previewHeroText}>Preview this course</Text>
                </TouchableOpacity>
              </View>
            </View>
            
            {/* Course Overview */}
            <View style={styles.courseOverview}>
              <Text style={[styles.overviewTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                Overview
              </Text>
              <Text style={[styles.overviewText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                {selectedCourse.longDescription}
              </Text>
            </View>
            
            {/* What You'll Learn */}
            <View style={styles.learningSection}>
              <Text style={[styles.learningTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                🎯 What You'll Learn
              </Text>
              {selectedCourse.learningOutcomes?.map((outcome, index) => (
                <View key={index} style={styles.learningItem}>
                  <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
                  <Text style={[styles.learningText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                    {outcome}
                  </Text>
                </View>
              ))}
            </View>
            
            {/* Course Modules */}
            <View style={styles.modulesSection}>
              <Text style={[styles.modulesTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                📚 Course Content
              </Text>
              {selectedCourse.modules?.map((module, index) => (
                <View key={module.id} style={[styles.moduleItem, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                  <View style={styles.moduleHeader}>
                    <Text style={[styles.moduleNumber, { color: selectedCourse.color }]}>
                      {index + 1}
                    </Text>
                    <View style={styles.moduleInfo}>
                      <Text style={[styles.moduleTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                        {module.title}
                      </Text>
                      <Text style={[styles.moduleDuration, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                        {module.duration} • {module.lessons} lessons
                      </Text>
                    </View>
                  </View>
                </View>
              ))}
              <Text style={[styles.modulesTotal, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {selectedCourse.totalLessons} lessons • {selectedCourse.duration} total
              </Text>
            </View>
            
            {/* Requirements */}
            <View style={styles.requirementsSection}>
              <Text style={[styles.requirementsTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                📋 Requirements
              </Text>
              {selectedCourse.requirements?.map((requirement, index) => (
                <View key={index} style={styles.requirementItem}>
                  <MaterialCommunityIcons name="check" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <Text style={[styles.requirementText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                    {requirement}
                  </Text>
                </View>
              ))}
            </View>
            
            {/* Instructor */}
            <View style={styles.instructorSection}>
              <Text style={[styles.instructorTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                👨‍🏫 Instructor
              </Text>
              <View style={[styles.instructorCard, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                <Image 
                  source={{ uri: (selectedCourse.instructorPhoto && typeof selectedCourse.instructorPhoto=="string" && selectedCourse.instructorPhoto.startsWith("http")) ? selectedCourse.instructorPhoto : undefined }} 
                  style={styles.instructorImage}
                />
                <View style={styles.instructorInfo}>
                  <Text style={[styles.instructorName, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedCourse.instructor}
                  </Text>
                  <Text style={[styles.instructorBio, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    {selectedCourse.instructorBio}
                  </Text>
                </View>
              </View>
            </View>
            
            {/* Reviews */}
            {selectedCourse.reviews && selectedCourse.reviews.length > 0 && (
              <View style={styles.reviewsSection}>
                <Text style={[styles.reviewsTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  ⭐ Reviews
                </Text>
                {selectedCourse.reviews.slice(0, 2).map((review) => (
                  <View key={review.id} style={[styles.reviewCard, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                    <View style={styles.reviewHeader}>
                      <View style={styles.reviewRating}>
                        {[...Array(5)].map((_, i) => (
                          <MaterialCommunityIcons 
                            key={i}
                            name="star" 
                            size={16} 
                            color={i < review.rating ? "#fbbf24" : (darkMode ? '#4a5568' : '#e5e7eb')} 
                          />
                        ))}
                      </View>
                      <Text style={[styles.reviewDate, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                        {review.date}
                      </Text>
                    </View>
                    <Text style={[styles.reviewUser, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {review.user}
                    </Text>
                    <Text style={[styles.reviewComment, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {review.comment}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </ScrollView>
          
          <View style={[styles.modalActions, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <View style={styles.priceSection}>
              {selectedCourse.isFree ? (
                <Text style={[styles.finalFreePrice, { color: '#10b981' }]}>Free</Text>
              ) : (
                <>
                  <Text style={[styles.finalPrice, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    ${selectedCourse.price}
                  </Text>
                  {selectedCourse.originalPrice > selectedCourse.price && (
                    <Text style={[styles.finalOriginalPrice, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      ${selectedCourse.originalPrice}
                    </Text>
                  )}
                </>
              )}
            </View>
            
            {selectedCourse.enrolled ? (
              <TouchableOpacity
                style={[styles.modalActionButton, styles.goToCourseButton]}
                onPress={() => {
                  setShowCourseDetails(false);
                  navigation.navigate('CourseContent', { course: selectedCourse });
                }}
              >
                <MaterialCommunityIcons name="play-circle" size={20} color="#ffffff" />
                <Text style={styles.goToCourseButtonText}>Go to Course</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[
                  styles.modalActionButton, 
                  selectedCourse.isFree ? styles.enrollNowFreeButton : styles.enrollNowPaidButton
                ]}
                onPress={() => {
                  setShowCourseDetails(false);
                  enrollInCourse(selectedCourse);
                }}
              >
                <MaterialCommunityIcons 
                  name={selectedCourse.isFree ? "download" : "cart-plus"} 
                  size={20} 
                  color="#ffffff" 
                />
                <Text style={styles.enrollNowButtonText}>
                  {selectedCourse.isFree ? 'Enroll for Free' : 'Enroll Now'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </SafeAreaView>
      )}
    </Modal>
  );

  const renderVideoModal = () => (
    <Modal
      visible={videoModalVisible}
      animationType="fade"
      onRequestClose={() => {
        setVideoModalVisible(false);
        if (videoRef.current) {
          videoRef.current.stopAsync();
        }
      }}
    >
      <SafeAreaView style={styles.videoModalContainer}>
        <View style={styles.videoHeader}>
          <TouchableOpacity onPress={() => setVideoModalVisible(false)}>
            <MaterialCommunityIcons name="close" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.videoTitle}>Course Preview</Text>
          <View style={{ width: 24 }} />
        </View>
        <Video
          ref={videoRef}
          source={{ uri: selectedPreviewVideo || undefined }}
          style={styles.videoPlayer}
          useNativeControls
          resizeMode="contain"
          isLooping={false}
          onPlaybackStatusUpdate={(status) => {
            setIsPlaying(status.isPlaying);
          }}
        />
      </SafeAreaView>
    </Modal>
  );

  const getFilterCount = () => {
    let count = 0;
    if (selectedFilters.category !== 'all') count++;
    if (selectedFilters.level !== 'all') count++;
    if (selectedFilters.price !== 'all') count++;
    if (selectedFilters.rating > 0) count++;
    if (selectedFilters.duration !== 'all') count++;
    return count;
  };

  if (loading) {
    return renderLoading();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar 
        barStyle="light-content" 
        backgroundColor="#667eea" 
      />
      
      {renderHeader()}
      {renderSearchBar()}
      
      <Animated.ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: false }
        )}
      >
        {renderTabs()}
        {renderCategoryFilters()}
        
        {/* Filter Summary */}
        {getFilterCount() > 0 && (
          <View style={styles.filterSummary}>
            <Text style={[styles.filterSummaryText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
              Active filters: {getFilterCount()}
            </Text>
            <TouchableOpacity onPress={resetFilters}>
              <Text style={styles.clearFiltersText}>Clear all</Text>
            </TouchableOpacity>
          </View>
        )}
        
        <View style={styles.coursesList}>
          <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
            {activeTab === 'all' ? 'All Courses' : 
             activeTab === 'trending' ? 'Trending Courses' :
             activeTab === 'new' ? 'New Courses' :
             activeTab === 'recommended' ? 'Recommended for You' : 'Free Courses'}
            <Text style={styles.courseCount}> ({filteredCourses.length})</Text>
          </Text>
          
          {filteredCourses.length === 0 ? (
            renderEmptyState()
          ) : (
            filteredCourses.map((course) => renderCourseCard(course))
          )}
        </View>
        
        {/* Categories Section */}
        <View style={styles.categoriesSection}>
          <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
            Browse by Category
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoriesScroll}>
            {categories.slice(1).map((category) => (
              <TouchableOpacity
                key={category.id}
                style={[
                  styles.categoryCard,
                  { backgroundColor: getCategoryColor(category.id) + '20' },
                ]}
                onPress={() => {
                  setSelectedFilters({...selectedFilters, category: category.id});
                  setActiveTab('all');
                }}
              >
                <LinearGradient
                  colors={[getCategoryColor(category.id), `${getCategoryColor(category.id)}80`]}
                  style={styles.categoryCardIcon}
                >
                  <MaterialCommunityIcons name={category.icon} size={32} color="#ffffff" />
                </LinearGradient>
                <Text style={[styles.categoryCardTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  {category.label}
                </Text>
                <Text style={[styles.categoryCardCount, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {courses.filter(c => c.category === category.id).length} courses
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
        
        {/* Spacer for bottom padding */}
        <View style={{ height: 100 }} />
      </Animated.ScrollView>
      
      {/* Modals */}
      {renderFiltersModal()}
      {renderCourseDetailsModal()}
      {renderVideoModal()}
      
      {/* Floating Action Button */}
      <TouchableOpacity
        style={[styles.fab, { backgroundColor: '#667eea' }]}
        onPress={() => navigation.navigate('MyCourses')}
      >
        <MaterialCommunityIcons name="book-open-variant" size={24} color="#ffffff" />
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#667eea',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    overflow: 'hidden',
  },
  headerGradient: {
    paddingHorizontal: 20,
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
    paddingBottom: 20,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  menuButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  iconButton: {
    padding: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    marginBottom: 16,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 16,
    padding: 12,
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  searchContainer: {
    position: 'absolute',
    top: 160,
    left: 20,
    right: 20,
    zIndex: 99,
    borderRadius: 16,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    marginHorizontal: 12,
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  filterBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#ef4444',
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterBadgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  scrollView: {
    flex: 1,
    marginTop: 180,
  },
  contentContainer: {
    paddingBottom: 100,
  },
  tabsContainer: {
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  categoryFilters: {
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  categoryTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 12,
  },
  categoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  categoryButtonText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  filterSummary: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginBottom: 16,
  },
  filterSummaryText: {
    fontSize: 14,
  },
  clearFiltersText: {
    fontSize: 14,
    color: '#667eea',
    fontWeight: '600',
  },
  coursesList: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  courseCount: {
    fontSize: 14,
    fontWeight: 'normal',
    opacity: 0.7,
  },
  courseCard: {
    marginBottom: 20,
  },
  courseCardContent: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  courseImageContainer: {
    height: 180,
    position: 'relative',
  },
  courseImage: {
    width: '100%',
    height: '100%',
  },
  courseBadges: {
    position: 'absolute',
    top: 12,
    left: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  freeBadge: {
    backgroundColor: '#10b981',
  },
  discountBadge: {
    backgroundColor: '#ef4444',
  },
  newBadge: {
    backgroundColor: '#667eea',
  },
  featuredBadge: {
    backgroundColor: '#f59e0b',
  },
  badgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  previewButton: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  courseContent: {
    padding: 16,
  },
  courseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  courseIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  courseTitleContainer: {
    flex: 1,
  },
  courseTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  courseInstructor: {
    fontSize: 12,
  },
  bookmarkButton: {
    padding: 4,
  },
  courseSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  courseDetails: {
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailText: {
    fontSize: 12,
    marginLeft: 4,
  },
  levelBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  levelText: {
    fontSize: 10,
    fontWeight: '600',
  },
  courseTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  tagText: {
    fontSize: 10,
  },
  courseFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  freePrice: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  currentPrice: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  originalPrice: {
    fontSize: 14,
    textDecorationLine: 'line-through',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    gap: 8,
  },
  enrollFreeButton: {
    backgroundColor: '#10b981',
  },
  enrollPaidButton: {
    backgroundColor: '#667eea',
  },
  continueButton: {
    backgroundColor: '#f59e0b',
  },
  enrollButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  continueButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
    paddingHorizontal: 20,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
  },
  emptyStateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  emptyStateButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  categoriesSection: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  categoriesScroll: {
    marginHorizontal: -20,
    paddingHorizontal: 20,
  },
  categoryCard: {
    width: 140,
    padding: 16,
    borderRadius: 16,
    marginRight: 12,
    alignItems: 'center',
  },
  categoryCardIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  categoryCardTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 4,
  },
  categoryCardCount: {
    fontSize: 12,
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  filtersModal: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.9,
  },
  filtersHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  filtersTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  filtersContent: {
    padding: 20,
  },
  filterSection: {
    marginBottom: 24,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  filterOptionText: {
    fontSize: 14,
    fontWeight: '500',
  },
  ratingFilter: {
    flexDirection: 'row',
    gap: 8,
  },
  ratingOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  ratingText: {
    fontSize: 14,
    fontWeight: '500',
  },
  filtersActions: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
  },
  filterActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    flex: 1,
    gap: 8,
  },
  resetButton: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    borderWidth: 1,
    borderColor: '#ef4444',
  },
  resetButtonText: {
    color: '#ef4444',
    fontSize: 16,
    fontWeight: '600',
  },
  applyButton: {
    backgroundColor: '#667eea',
  },
  applyButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  modalContainer: {
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: Platform.OS === 'ios' ? 40 : 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  modalContent: {
    flex: 1,
  },
  courseHero: {
    height: 280,
    position: 'relative',
  },
  courseHeroImage: {
    width: '100%',
    height: '100%',
  },
  courseHeroGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 100,
  },
  courseHeroContent: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 20,
  },
  courseHeroHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  courseHeroIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  courseHeroInfo: {
    flex: 1,
  },
  courseHeroTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  courseHeroInstructor: {
    fontSize: 16,
  },
  courseHeroStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  heroStat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  heroStatText: {
    fontSize: 14,
    fontWeight: '500',
  },
  previewHeroButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    alignSelf: 'flex-start',
    gap: 8,
  },
  previewHeroText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  courseOverview: {
    padding: 20,
  },
  overviewTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  overviewText: {
    fontSize: 16,
    lineHeight: 24,
  },
  learningSection: {
    padding: 20,
    paddingTop: 0,
  },
  learningTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  learningItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    gap: 8,
  },
  learningText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  modulesSection: {
    padding: 20,
    paddingTop: 0,
  },
  modulesTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  moduleItem: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 8,
  },
  moduleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  moduleNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    marginRight: 16,
  },
  moduleInfo: {
    flex: 1,
  },
  moduleTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  moduleDuration: {
    fontSize: 12,
  },
  modulesTotal: {
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
  },
  requirementsSection: {
    padding: 20,
    paddingTop: 0,
  },
  requirementsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  requirementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  requirementText: {
    flex: 1,
    fontSize: 14,
  },
  instructorSection: {
    padding: 20,
    paddingTop: 0,
  },
  instructorTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  instructorCard: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  instructorImage: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginRight: 16,
  },
  instructorInfo: {
    flex: 1,
  },
  instructorName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  instructorBio: {
    fontSize: 14,
    lineHeight: 20,
  },
  reviewsSection: {
    padding: 20,
    paddingTop: 0,
  },
  reviewsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  reviewCard: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  reviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  reviewRating: {
    flexDirection: 'row',
  },
  reviewDate: {
    fontSize: 12,
  },
  reviewUser: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  reviewComment: {
    fontSize: 14,
    lineHeight: 20,
  },
  modalActions: {
    flexDirection: 'row',
    padding: 20,
    gap: 16,
    alignItems: 'center',
  },
  priceSection: {
    flex: 1,
  },
  finalFreePrice: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  finalPrice: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  finalOriginalPrice: {
    fontSize: 16,
    textDecorationLine: 'line-through',
  },
  modalActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 12,
    flex: 2,
    gap: 8,
  },
  enrollNowFreeButton: {
    backgroundColor: '#10b981',
  },
  enrollNowPaidButton: {
    backgroundColor: '#667eea',
  },
  goToCourseButton: {
    backgroundColor: '#f59e0b',
  },
  enrollNowButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  goToCourseButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  videoModalContainer: {
    flex: 1,
    backgroundColor: '#000000',
  },
  videoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#1f2937',
  },
  videoTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  videoPlayer: {
    flex: 1,
    backgroundColor: '#000000',
  },
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
});

export default CourseCatalog;
